import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import queryString from 'query-string';
import { UserInfo } from '../common/model/dto/user-info';
import { UserCycle } from '../common/model/dto/user-cycle/user-cycle';
import { HormoneSummaryResponse } from '../common/model/dto/hormone-summary';
import { Paginated } from '../common/types';
import {
  ChartRangeMode,
  CustomChartRangeSelection,
} from '../common/model/type/chart-range-selection.type';
import ChartUtils from '../common/utils/services/chart-utils';
import { mapHormoneSummaryToViewModel } from '../common/utils/services/hormone-summary-utils';

const FINISHED_CYCLES_NEWEST_FIRST = queryString.stringify({
  limit: 0,
  order_by: 'start_day',
  order_dir: 'desc',
});

export function useCycleSelection(
  user: UserInfo | undefined,
  fetchCycles?: (query: string) => Promise<Paginated<UserCycle>>,
  fetchCycleHormoneSummary?: (
    cycleId: string
  ) => Promise<HormoneSummaryResponse>,
  cycleQueryKey?: unknown[]
) {
  const [rangeMode, setRangeMode] = useState<ChartRangeMode>(
    fetchCycles ? 'cycle' : 'custom'
  );
  const [customRangeSelection, setCustomRangeSelection] =
    useState<CustomChartRangeSelection>({ type: 'preset', days: 30 });
  const [selectedCycle, setSelectedCycle] = useState<UserCycle>();
  const [initializedCycleContext, setInitializedCycleContext] =
    useState<string>();
  const [repairingSummaryCycleId, setRepairingSummaryCycleId] =
    useState<string>();
  const handledSummary404CycleId = useRef<string>();

  const cycleContextKey = fetchCycles
    ? JSON.stringify(cycleQueryKey ?? [])
    : undefined;
  const cyclesQuery = useQuery({
    queryKey: [...(cycleQueryKey ?? []), FINISHED_CYCLES_NEWEST_FIRST],
    queryFn: () => fetchCycles!(FINISHED_CYCLES_NEWEST_FIRST),
    enabled: !!fetchCycles,
  });
  const refetchCycles = cyclesQuery.refetch;
  const finishedCycles = useMemo(
    () => cyclesQuery.data?.docs ?? [],
    [cyclesQuery.data?.docs]
  );
  const isRangeInitialized =
    !fetchCycles || initializedCycleContext === cycleContextKey;
  const summaryCycleId =
    fetchCycleHormoneSummary &&
    isRangeInitialized &&
    rangeMode === 'cycle' &&
    selectedCycle
      ? selectedCycle.id
      : undefined;
  const summaryQuery = useQuery({
    queryKey: [...(cycleQueryKey ?? []), 'summary', summaryCycleId],
    queryFn: () => fetchCycleHormoneSummary!(summaryCycleId!),
    enabled: !!fetchCycleHormoneSummary && !!summaryCycleId,
    retry: (failureCount, error) => {
      const status = axios.isAxiosError(error) ? error.response?.status : null;

      if (status === 403 || status === 404) {
        return false;
      }

      return failureCount < 2;
    },
  });
  const phaseDays = useMemo(
    () => ({
      follicular: summaryQuery.data?.follicular_phase ?? null,
      ovulation: summaryQuery.data?.ovulation_phase ?? null,
      luteal: summaryQuery.data?.luteal_phase ?? null,
    }),
    [
      summaryQuery.data?.follicular_phase,
      summaryQuery.data?.ovulation_phase,
      summaryQuery.data?.luteal_phase,
    ]
  );
  const summaryViewModel = useMemo(
    () =>
      summaryQuery.data && user
        ? mapHormoneSummaryToViewModel(summaryQuery.data, user)
        : undefined,
    [summaryQuery.data, user]
  );

  useEffect(() => {
    if (
      !fetchCycles ||
      !cycleContextKey ||
      !cyclesQuery.data ||
      initializedCycleContext === cycleContextKey
    ) {
      return;
    }

    const newestCycle = cyclesQuery.data.docs[0];
    setRepairingSummaryCycleId(undefined);
    handledSummary404CycleId.current = undefined;

    if (newestCycle) {
      setSelectedCycle(newestCycle);
      setRangeMode('cycle');
    } else {
      setSelectedCycle(undefined);
      setCustomRangeSelection({ type: 'preset', days: 30 });
      setRangeMode('custom');
    }

    setInitializedCycleContext(cycleContextKey);
  }, [cycleContextKey, fetchCycles, cyclesQuery.data, initializedCycleContext]);

  useEffect(() => {
    const isNotFound =
      axios.isAxiosError(summaryQuery.error) &&
      summaryQuery.error.response?.status === 404;

    if (
      !summaryCycleId ||
      !isNotFound ||
      handledSummary404CycleId.current === summaryCycleId
    ) {
      return;
    }

    handledSummary404CycleId.current = summaryCycleId;
    setRepairingSummaryCycleId(summaryCycleId);
    let isActive = true;

    void refetchCycles().then((result) => {
      if (!isActive) {
        return;
      }

      if (result.isError) {
        setRepairingSummaryCycleId(undefined);
        return;
      }

      const replacementCycle = result.data?.docs.find(
        (cycle) => cycle.id !== summaryCycleId
      );
      if (replacementCycle) {
        setSelectedCycle(replacementCycle);
        setRangeMode('cycle');
      } else {
        setSelectedCycle(undefined);
        setCustomRangeSelection({ type: 'preset', days: 30 });
        setRangeMode('custom');
      }

      setRepairingSummaryCycleId(undefined);
    });

    return () => {
      isActive = false;
    };
  }, [refetchCycles, summaryCycleId, summaryQuery.error]);

  const customDateRange = useMemo(
    () => ChartUtils.getCustomDateRange(customRangeSelection),
    [customRangeSelection]
  );
  const dateRange = useMemo(() => {
    if (rangeMode === 'cycle' && selectedCycle) {
      return ChartUtils.getCycleDateRange(selectedCycle);
    }

    return customDateRange;
  }, [customDateRange, rangeMode, selectedCycle]);

  const onModeChange = (mode: ChartRangeMode) => {
    setRepairingSummaryCycleId(undefined);
    setRangeMode(mode);

    if (mode === 'cycle' && !selectedCycle) {
      setSelectedCycle(finishedCycles[0]);
    }
  };

  const onCycleSelect = (cycle: UserCycle) => {
    setRepairingSummaryCycleId(undefined);
    handledSummary404CycleId.current = undefined;
    setSelectedCycle(cycle);
  };

  const onCustomSelectionChange = (selection: CustomChartRangeSelection) => {
    setCustomRangeSelection(selection);
  };

  const onSummaryRetry = () => {
    handledSummary404CycleId.current = undefined;
    void summaryQuery.refetch();
  };

  const shouldRenderHormoneSummary =
    !!fetchCycles &&
    isRangeInitialized &&
    rangeMode === 'cycle' &&
    !!selectedCycle;

  return {
    rangeMode,
    customRangeSelection,
    customDateRange,
    dateRange,
    selectedCycle,
    finishedCycles,
    cyclesLoading: cyclesQuery.isPending,
    isRangeInitialized,
    phaseDays,
    summaryViewModel,
    isSummaryLoading: summaryQuery.isPending || !!repairingSummaryCycleId,
    shouldRenderHormoneSummary,
    onModeChange,
    onCycleSelect,
    onCustomSelectionChange,
    onSummaryRetry,
  };
}
